import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { X, Send, MessageSquare, Loader2, Wand2, Brain, Trash2, Compass, Search, Receipt, FileText, LayoutDashboard, ArrowRight, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CaraAvatar } from './CaraAvatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MARKDOWN_COMPONENTS = {
  p: ({ children }) => <p className="text-sm mb-2 last:mb-0 leading-relaxed">{children}</p>,
  a: ({ href, children }) => <a href={href} className="text-cn-primary font-semibold underline">{children}</a>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold text-cn-primary">{children}</strong>,
};

const QUICK_DEFAULT = [
  { label: 'How do I book a visit?', mode: 'general' },
  { label: 'Reschedule my appointment', mode: 'general' },
  { label: 'Where do I see my invoice?', mode: 'general' },
  { label: 'Help me find a clinician', mode: 'general' },
];

// Context-aware suggested actions — adapt to the page the user is on.
const getQuickActions = (pathname = '') => {
  const p = pathname.toLowerCase();
  if (/(billing|rcm|statement|invoice|payment|claim)/.test(p)) {
    return [
      { label: 'Explain this charge.', mode: 'general' },
      { label: 'How do I pay my balance?', mode: 'general' },
      { label: 'What does my insurance cover?', mode: 'general' },
      { label: 'Where do I download my statement?', mode: 'general' },
    ];
  }
  if (/(telehealth)/.test(p)) {
    return [
      { label: 'How do I join my video visit?', mode: 'general' },
      { label: 'Test my camera and microphone', mode: 'general' },
      { label: 'Can I reschedule this visit?', mode: 'general' },
    ];
  }
  if (/(\/book|clinician|provider|find-clinicians|match)/.test(p)) {
    return [
      { label: 'Is this provider in-network?', mode: 'general' },
      { label: 'What are their credentials?', mode: 'general' },
      { label: 'Help me book a visit', mode: 'general' },
      { label: 'Compare these clinicians', mode: 'general' },
    ];
  }
  if (/(dashboard|^\/$|\/patient|\/clinician|\/agency|\/discharge)/.test(p)) {
    return [
      { label: 'How do I find a nurse?', mode: 'general' },
      { label: 'What should I do next?', mode: 'general' },
      { label: 'Explain my care plan', mode: 'general' },
      { label: 'How does booking work?', mode: 'general' },
    ];
  }
  return QUICK_DEFAULT;
};

// Role-aware navigation shortcuts — turns Cara into the front door for each role's
// highest-value flow (patient bookings, clinician activation, agency staffing, planner referrals).
const getRoleActions = (role) => {
  switch (role) {
    case 'patient':
      return {
        primary: { label: 'Find the right care', sub: 'Guided match → book a clinician', to: '/patient/navigator', icon: Compass },
        secondary: [
          { label: 'Browse clinicians', to: '/patient/find-clinicians', icon: Search },
          { label: 'My billing', to: '/patient/billing', icon: Receipt },
        ],
      };
    case 'clinician':
      return {
        primary: { label: 'Set up my practice', sub: 'Practice Coach → profile & packages', to: '/clinician/coach', icon: Compass },
        secondary: [
          { label: 'Bill a visit', to: '/clinician/billing', icon: Receipt },
          { label: 'Clinical notes', to: '/clinician/notes', icon: FileText },
        ],
      };
    case 'agency':
      return {
        primary: { label: 'Find clinicians to staff', sub: 'Search & send engagement requests', to: '/agency/find-clinicians', icon: Search },
        secondary: [
          { label: 'Agency dashboard', to: '/agency/dashboard', icon: LayoutDashboard },
        ],
      };
    case 'discharge_planner':
      return {
        primary: { label: 'Refer a patient', sub: 'Add to your referral pipeline', to: '/planner/dashboard?refer=1', icon: UserPlus },
        secondary: [
          { label: 'My pipeline', to: '/planner/dashboard', icon: LayoutDashboard },
        ],
      };
    case 'admin':
      return {
        primary: { label: 'Open admin dashboard', sub: 'Users, claims, revenue & risk', to: '/admin/dashboard', icon: LayoutDashboard },
        secondary: [],
      };
    default:
      return null;
  }
};

/**
 * Cara — Floating AI Copilot. Page-aware, voice-optional (text first),
 * with calm sage-green personality. Always visible for authenticated users.
 */
export const LynkCopilot = () => {
  const { user, token } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [memories, setMemories] = useState([]);
  const [showMemoryMgr, setShowMemoryMgr] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  // Allow any "Ask Cara" button across the app to open the assistant.
  useEffect(() => {
    const openCara = () => setOpen(true);
    window.addEventListener('cara:open', openCara);
    return () => window.removeEventListener('cara:open', openCara);
  }, []);

  // Load memories when panel opens and after each send
  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/lynk/memory`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setMemories(r.data.facts || []))
      .catch(() => {});
  }, [open, token, busy]);

  if (!user || !token) return null;

  const send = async (text, modeOverride) => {
    const message = (text || input).trim();
    if (!message) return;
    const next = [...messages, { role: 'user', content: message }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await axios.post(`${API}/lynk/chat`, {
        message,
        session_id: sessionId,
        page_route: location.pathname,
        page_title: document.title,
        mode: modeOverride || 'general',
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSessionId(res.data.session_id);
      setMessages([...next, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages([...next, { role: 'assistant', content: "I had a hiccup. Try again in a moment — or rephrase your question and I'll do my best." }]);
    } finally {
      setBusy(false);
    }
  };

  const forgetFact = async (key) => {
    try {
      await axios.delete(`${API}/lynk/memory/${encodeURIComponent(key)}`, { headers: { Authorization: `Bearer ${token}` } });
      setMemories((prev) => prev.filter((m) => m.key !== key));
    } catch (e) { /* swallow */ }
  };

  const goTo = (to) => { setOpen(false); navigate(to); };
  const roleActions = getRoleActions(user.role);

  const memorySummary = memories.length === 0
    ? null
    : memories.length === 1
      ? memories[0].value
      : `${memories[memories.length - 1].value.slice(0, 60)}${memories[memories.length - 1].value.length > 60 ? '…' : ''}  ·  +${memories.length - 1} more`;

  const greeting = `Hi ${user.first_name || 'there'} — I'm Cara. Ask me anything about CaraLoom, or just tell me what you're trying to do.`;

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        data-testid="lynk-fab"
        aria-label="Open Cara AI assistant"
        className={`fixed z-[60] bottom-5 right-5 sm:bottom-7 sm:right-7 group ${open ? 'hidden' : 'flex'} items-center gap-2 pl-3 pr-4 py-3 rounded-full text-white font-semibold shadow-xl shadow-cn-primary/30 hover:shadow-cn-primary/50 transition-all hover:-translate-y-0.5`}
        style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
      >
        <span className="relative flex h-9 w-9 items-center justify-center">
          <CaraAvatar size={36} onDark />
          <span className="absolute inset-0 rounded-full ring-2 ring-white/30 animate-pulse-soft" />
        </span>
        <span className="text-sm">Ask Cara</span>
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed z-[60] inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[420px] sm:max-h-[78vh] flex flex-col rounded-t-3xl sm:rounded-3xl border border-cn-border/60 bg-white dark:bg-[#0F172A] shadow-2xl shadow-cn-primary/20 overflow-hidden animate-fade-in-up" data-testid="lynk-panel">
          {/* Header */}
          <div className="p-4 sm:p-5 text-white" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 60%, #334155 100%)' }}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <CaraAvatar size={40} onDark className="rounded-full" />
                <div>
                  <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-white/70">Cara · always on</p>
                  <h3 className="font-display font-extrabold text-lg leading-tight">Your CaraLoom copilot</h3>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close Cara" data-testid="lynk-close" className="p-1.5 rounded-full hover:bg-white/15">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Memory strip */}
          {memorySummary && (
            <button
              onClick={() => setShowMemoryMgr(true)}
              data-testid="lynk-memory-strip"
              className="flex items-center gap-2 px-4 py-2.5 bg-cn-primary/8 dark:bg-cn-primary/20 border-b border-cn-border/60 hover:bg-cn-primary/12 transition-colors text-left w-full"
            >
              <Brain className="w-3.5 h-3.5 text-cn-primary flex-shrink-0" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-cn-primary">Cara remembers</span>
              <span className="text-xs text-cn-text dark:text-white truncate flex-1">{memorySummary}</span>
              <span className="text-[10px] text-cn-primary font-bold whitespace-nowrap">Manage</span>
            </button>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-cn-bg/40 dark:bg-[#020617]/40">
            {messages.length === 0 && (
              <>
                <div className="rounded-2xl bg-white dark:bg-white/5 border border-cn-border/60 p-4">
                  <p className="text-sm text-cn-text dark:text-white leading-relaxed">{greeting}</p>
                </div>
                {roleActions && (
                  <div className="grid grid-cols-1 gap-2 pt-1" data-testid="lynk-role-actions">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted px-1">Quick actions</p>
                    <button
                      onClick={() => goTo(roleActions.primary.to)}
                      data-testid="lynk-role-primary"
                      className="group flex items-center gap-3 text-left px-3.5 py-3 rounded-2xl text-white shadow-lg shadow-cn-primary/25 hover:-translate-y-0.5 transition-transform"
                      style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}
                    >
                      <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                        {React.createElement(roleActions.primary.icon, { className: 'w-4 h-4' })}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display font-bold text-sm leading-tight">{roleActions.primary.label}</span>
                        {roleActions.primary.sub && <span className="block text-[11px] text-white/70 truncate">{roleActions.primary.sub}</span>}
                      </span>
                      <ArrowRight className="w-4 h-4 text-white/70 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                    </button>
                    {roleActions.secondary.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {roleActions.secondary.map((a, i) => (
                          <button
                            key={i}
                            onClick={() => goTo(a.to)}
                            data-testid={`lynk-role-secondary-${i}`}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white dark:bg-white/5 border border-cn-border/60 hover:border-cn-primary/40 hover:bg-cn-primary/5 transition-colors text-cn-text dark:text-white"
                          >
                            {React.createElement(a.icon, { className: 'w-3.5 h-3.5 text-cn-primary' })}{a.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 gap-2 pt-1">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted px-1">Suggested for this page</p>
                  {getQuickActions(location.pathname).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => send(q.label, q.mode)}
                      data-testid={`lynk-quick-${i}`}
                      className="text-left text-sm px-3 py-2.5 rounded-2xl bg-white dark:bg-white/5 border border-cn-border/60 hover:border-cn-primary/40 hover:bg-cn-primary/5 transition-colors text-cn-text dark:text-white"
                    >
                      <Wand2 className="w-3.5 h-3.5 text-cn-primary inline mr-2" />{q.label}
                    </button>
                  ))}
                </div>
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex items-end gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && <CaraAvatar size={26} className="flex-shrink-0 mb-0.5" />}
                <div className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm ${
                  m.role === 'user'
                    ? 'bg-cn-primary text-white rounded-br-md'
                    : 'bg-white dark:bg-white/5 text-cn-text dark:text-white border border-cn-border/60 rounded-bl-md'
                }`}>
                  {m.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>{m.content}</ReactMarkdown>
                    </div>
                  ) : <p className="whitespace-pre-wrap">{m.content}</p>}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-sm text-cn-text-muted px-2">
                <CaraAvatar size={22} className="flex-shrink-0" />
                <Loader2 className="w-4 h-4 animate-spin text-cn-primary" /> Cara is thinking…
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-cn-border/60 bg-white dark:bg-[#0F172A]">
            <div className="flex items-center gap-2 bg-cn-bg dark:bg-white/5 border border-cn-border/60 rounded-2xl p-1.5 focus-within:border-cn-primary/40">
              <MessageSquare className="w-4 h-4 ml-2 text-cn-text-muted" />
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="Ask anything — I'm listening"
                data-testid="lynk-input"
                className="flex-1 bg-transparent outline-none text-sm text-cn-text dark:text-white placeholder:text-cn-text-muted"
                disabled={busy}
              />
              <button
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="lynk-send"
                className="p-2 rounded-xl text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #0F172A, #334155)' }}
                aria-label="Send message to Cara"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-cn-text-muted mt-1.5 text-center">Cara is AI · not a substitute for clinical care · for emergencies, call 911</p>
          </div>

          {/* Memory Manager (overlay inside panel) */}
          {showMemoryMgr && (
            <div className="absolute inset-0 bg-white dark:bg-[#0F172A] flex flex-col z-10 animate-fade-in-up" data-testid="lynk-memory-mgr">
              <div className="p-4 border-b border-cn-border/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-cn-primary" />
                  <h4 className="font-display font-bold text-base">What Cara remembers</h4>
                </div>
                <button onClick={() => setShowMemoryMgr(false)} data-testid="lynk-memory-close" className="p-1.5 rounded-full hover:bg-cn-border/40" aria-label="Back to chat">
                  <X className="w-4 h-4 text-cn-text-muted" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {memories.length === 0 ? (
                  <p className="text-sm text-cn-text-muted text-center py-8">Nothing remembered yet. Tell Cara something to start.</p>
                ) : memories.map((m) => (
                  <div key={m.key} className="flex items-start gap-3 p-3 rounded-2xl bg-cn-bg dark:bg-white/5 border border-cn-border/60" data-testid={`lynk-memory-item-${m.key}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">{m.source}</p>
                      <p className="text-sm text-cn-text dark:text-white mt-0.5">{m.value}</p>
                    </div>
                    <button onClick={() => forgetFact(m.key)} data-testid={`lynk-forget-${m.key}`} aria-label="Forget this fact" className="p-1.5 rounded-full text-cn-text-muted hover:bg-cn-error/10 hover:text-cn-error">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="px-4 py-3 text-[10px] text-cn-text-muted border-t border-cn-border/60">Only you can see this. Anything you forget is deleted from Cara&apos;s memory immediately.</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default LynkCopilot;
